import cn from 'classnames';
import { ComponentProps, useEffect, useId, useState } from 'react';
import { useStore } from 'effector-react';
import { $agricultureById } from '@@/agriculture/store/agriculture.store';
import { numberWithSpaces } from '@lib/number-formatting';
import { IAgriculture } from '@@/agriculture/ts';
import { Card } from '@ui/card';
import { UiButton } from '@@/ui/components/ui-button';
import { ISimpleMixingBagCardBagProps, SimpleMixingBagCardBag } from './components';
import { UiTextField } from '@@/ui/components/ui-text-field';
import { IBag } from '@@/bags/ts';
import { ISimpleMixingBag } from '@@/mixing/ts';
import { SIMPLE_MIXING_BAG_INFO_LIMITS } from '@@/mixing/constants/simple-mixing.constants';

import styles from './index.module.sass';

export function SimpleMixingBagCard(props: ISimpleMixingBagCardProps) {
  const {
    className,
    style,
    data,
    weight = 0,
    price = 0,
    onWeightChange,
    onPriceChange,
    onSelect,
    onDelete,
    ...rest
  } = props;
  const rootClass = cn(className, styles.root);

  const agricultureById = useStore($agricultureById);
  const culture = agricultureById[data?.agricultureId as number] as
    | IAgriculture
    | undefined;
  const [isFilled, setIsFilled] = useState(false); // by fill button
  const calculatedPrice = price * weight;

  const formId = useId();
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsFilled(true);
  };
  const onEdit = () => setIsFilled(false);
  const onDeleteLocal = () => {
    setIsFilled(false);
    onDelete?.();
  };

  const [isCheckedData, setIsCheckedData] = useState(false);
  useEffect(() => {
    if (
      !isCheckedData &&
      data &&
      weight &&
      weight <= data.weight &&
      weight >= SIMPLE_MIXING_BAG_INFO_LIMITS.weight.min
    )
      setIsFilled(true);

    if (data) setIsCheckedData(true);
  }, [data]);

  return (
    <Card
      className={rootClass}
      style={{
        ...(isFilled && { backgroundColor: culture?.color }),
        ...style,
      }}
      {...rest}
    >
      <SimpleMixingBagCardBag
        data={data}
        filled={isFilled}
        onEdit={onEdit}
        onSelect={onSelect}
        onDelete={onDeleteLocal}
      />

      {!isFilled && (
        <form id={formId} className={styles.inputs} onSubmit={onSubmit}>
          <UiTextField
            size="s"
            label="вес к миксованию"
            rightSide="т"
            value={weight?.toString()}
            min={SIMPLE_MIXING_BAG_INFO_LIMITS.weight.min}
            max={data?.weight}
            type="number"
            required
            disabled={!data}
            onChange={({ value }) => onWeightChange?.(Number(value))}
          />
          <UiTextField
            size="s"
            label="стоимость"
            rightSide="₽/т"
            value={price?.toString()}
            min={SIMPLE_MIXING_BAG_INFO_LIMITS.price.min}
            max={SIMPLE_MIXING_BAG_INFO_LIMITS.price.max}
            type="number"
            disabled={!data}
            onChange={({ value }) => onPriceChange?.(Number(value))}
          />
        </form>
      )}

      {!isFilled && (
        <UiButton
          className={styles.submit}
          view="primary"
          label="Заполнить"
          disabled={!data}
          type="submit"
          htmlForm={formId}
        />
      )}

      {isFilled && (
        <table className={styles.filledInfo}>
          <tbody>
            {[
              { label: 'вес к миксованию', value: numberWithSpaces(weight) + 'т' },
              { label: 'стоимость', value: numberWithSpaces(price) + '₽' },
              { label: 'общая сумма', value: numberWithSpaces(calculatedPrice) + '₽' },
            ].map((item, index) => (
              <tr key={index} className={styles.filledItem}>
                <td className={styles.filledItem__label}>{item.label}</td>
                <td className={styles.filledItem__value}>{item.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export interface ISimpleMixingBagCardProps
  extends Omit<ComponentProps<typeof Card>, 'children' | 'onSelect'> {
  data: IBag | ISimpleMixingBag | null;
  weight?: number;
  price?: number;
  onWeightChange?: (weight: number) => void;
  onPriceChange?: (price: number) => void;
  onSelect?: ISimpleMixingBagCardBagProps['onSelect'];
  onDelete?: ISimpleMixingBagCardBagProps['onDelete'];
}
